import api from './api';

export interface ConflictSummary {
  total: number;
  conflictDays: number; // Number of unique days with conflicts
  byStatus: Record<string, number>;
  bySeverity: Record<string, number>;
  upcomingCount: number;
  playersAffected: number;
  nextConflictDate: string | null;
}

export interface TimeConflict {
  type: 'DIRECT_OVERLAP' | 'PREPARATION_CONFLICT' | 'TRAVEL_BUFFER' | 'POTENTIAL_CONFLICT' | 'SIMULTANEOUS';
  severity: 'MINIMAL' | 'MODERATE' | 'DIRECT';
  timeDifference: number;
  bufferTime: number; // deprecated, kept for compatibility
  isPotential?: boolean;
  travelTime?: {
    durationSeconds: number;
    distanceMeters: number;
  };
  availableTravelWindow?: number;
  travelTimeUtilization?: number; // deprecated, kept for compatibility
  travelWindowBuffer?: number; // NEW: minutes of buffer (positive) or shortage (negative)
  isSimultaneous?: boolean; // NEW: true when matches start at same time
}

export interface ConflictMatch {
  match: {
    id: string;
    dateTime: string;
    homeTeam: string;
    awayTeam: string;
    location?: string;
    matchType: string;
    status?: string;
  };
  playerId: string;
  playerName: string;
  playerNames?: string[];  // All player names in this match (when multiple players share it)
  leagueName: string;
}

export interface DetectedConflict {
  id: string;
  match1: ConflictMatch;
  match2: ConflictMatch;
  timeConflict: TimeConflict;
  severity: 'MINIMAL' | 'MODERATE' | 'DIRECT';
  // Remove description - frontend will generate it
}

export interface ConflictGroup {
  id: string;
  date: string;
  conflicts: DetectedConflict[];
  playersAffected: string[];
  severity: 'MINIMAL' | 'MODERATE' | 'DIRECT';
  status: 'UNRESOLVED' | 'RESOLVED' | 'IGNORED';
}

export interface UserConflicts {
  parentId: string; // Keep field name for API compatibility
  totalConflicts: number;
  conflictGroups: ConflictGroup[];
  upcomingConflicts: ConflictGroup[];
  summary: ConflictSummary;
}

export interface ConflictResolution {
  resolutionType: 'ATTEND_FIRST' | 'ATTEND_SECOND' | 'SPLIT_TIME' | 'DELEGATE' | 'SKIP_BOTH';
  notes?: string;
  notifyCoaches?: boolean;
}

export interface ResolutionSuggestion {
  type: string;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high';
  reasoning: string;
}

export interface ConflictDetails {
  conflict: ConflictGroup;
  resolutionSuggestions: ResolutionSuggestion[];
  impactAnalysis: {
    playersAffected: number;
    matchesInvolved: number;
    severity: string;
    timeWindow: string;
    recommendations: string[];
  };
}

class ConflictService {
  /**
   * Get all conflicts for the current parent
   */
  async getConflicts(filters?: {
    playerId?: string;
    status?: string;
    severity?: string;
  }): Promise<UserConflicts> {
    const params = new URLSearchParams();
    if (filters?.playerId) params.append('playerId', filters.playerId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.severity) params.append('severity', filters.severity);

    const response = await api.get(`/conflicts?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get conflict summary for dashboard
   */
  async getConflictSummary(): Promise<ConflictSummary> {
    const response = await api.get('/conflicts/summary');
    return response.data.data;
  }

  /**
   * Fast conflict count from cache — returns null if no cache exists yet
   */
  async getConflictCount(): Promise<number | null> {
    const response = await api.get('/conflicts/summary/count');
    return response.data.data?.count ?? null;
  }

  /**
   * Get detailed information about a specific conflict
   */
  async getConflictDetails(conflictId: string): Promise<ConflictDetails> {
    const response = await api.get(`/conflicts/${conflictId}`);
    return response.data.data;
  }

  /**
   * Resolve a conflict with resolution details
   */
  async resolveConflict(conflictId: string, resolution: ConflictResolution): Promise<void> {
    await api.post(`/conflicts/${conflictId}/resolve`, resolution);
  }

  /**
   * Update conflict status
   */
  async updateConflictStatus(conflictId: string, status: 'UNRESOLVED' | 'RESOLVED' | 'IGNORED'): Promise<void> {
    await api.put(`/conflicts/${conflictId}/status`, { status });
  }

  /**
   * Ignore a conflict
   */
  async ignoreConflict(conflictId: string, reason?: string): Promise<void> {
    await api.post(`/conflicts/${conflictId}/ignore`, { reason });
  }

  /**
   * Force refresh of conflict detection
   */
  async refreshConflicts(): Promise<UserConflicts> {
    const response = await api.post('/conflicts/refresh');
    return response.data.data;
  }

  /**
   * Get conflicts for a specific player
   */
  async getPlayerConflicts(playerId: string): Promise<DetectedConflict[]> {
    const response = await api.get(`/conflicts?playerId=${playerId}`);
    return response.data.data.conflicts;
  }
}

export const conflictService = new ConflictService();
